import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Breadcrumbs,
  Link,
  Stack,
  Button,
  Alert,
} from "@mui/material";
import {
  Dashboard as ExecIcon,
  EventNote as FuneralIcon,
  Hotel as MortuaryIcon,
  Restaurant as ServiceIcon,
  DirectionsCar as FleetIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  Assessment as AdditionalIcon,
} from "@mui/icons-material";
import { IFASReportFilterBar } from "./components/IFASReportFilterBar";
import { FuneralManagementReportsTab } from "./tabs/FuneralManagementReportsTab";
import { MortuaryReportsTab } from "./tabs/MortuaryReportsTab";
import { ServicesCateringReportsTab } from "./tabs/ServicesCateringReportsTab";
import { FleetWarehouseReportsTab } from "./tabs/FleetWarehouseReportsTab";
import { IFASExecutiveDashboardTab } from "./tabs/IFASExecutiveDashboardTab";
import { AdditionalReportsTab } from "./tabs/AdditionalReportsTab";
import {
  ifasReportsService,
  IFASReportFilter,
  FuneralRegisterReport,
  PlannerPerformanceReport,
  MortuaryOccupancyReport,
  ServiceCateringReport,
  FleetLogisticsReport,
  WarehouseInventoryReport,
  IFASExecutiveDashboard,
  IFASAdditionalReport,
} from "../../../../services/ifasReportsService";
import { useSessionStore } from "../../../../store/useSessionStore";
import { useUserStore } from "../../../../store/useUserStore";
import { canAccessIfasReport, IfasReportKey } from "../../../../utils/ifasAccess";
import { ifasColors } from "../components/OperationsUI";

export const IFASReportingHub: React.FC = () => {
  const { userRole } = useSessionStore();
  const storedRole = useUserStore((state) => state.role);
  const effectiveRole = userRole || storedRole;
  const canView = useCallback((key: IfasReportKey) => canAccessIfasReport(effectiveRole, key), [effectiveRole]);
  const tabs = [
    { id: "funeral", visible: canView("funeral") || canView("planner") },
    { id: "mortuary", visible: canView("mortuary") || canView("mortuaryTrends") },
    { id: "services", visible: canView("services") },
    { id: "fleetWarehouse", visible: canView("fleet") || canView("warehouse") },
    { id: "executive", visible: canView("executive") },
    { id: "additional", visible: canView("inventoryMovements") || canView("suppliers") || canView("customers") || canView("feedback") },
  ].filter((tab) => tab.visible);
  const [tabIndex, setTabIndex] = useState<string>(tabs[0]?.id || "");
  const [filter, setFilter] = useState<IFASReportFilter>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Report States
  const [funerals, setFunerals] = useState<FuneralRegisterReport[]>([]);
  const [planners, setPlanners] = useState<PlannerPerformanceReport[]>([]);
  const [mortuaries, setMortuaries] = useState<MortuaryOccupancyReport[]>([]);
  const [services, setServices] = useState<ServiceCateringReport[]>([]);
  const [fleet, setFleet] = useState<FleetLogisticsReport[]>([]);
  const [inventory, setInventory] = useState<WarehouseInventoryReport[]>([]);
  const [dashboard, setDashboard] = useState<IFASExecutiveDashboard | null>(null);
  const [inventoryMovements, setInventoryMovements] = useState<IFASAdditionalReport[]>([]);
  const [suppliers, setSuppliers] = useState<IFASAdditionalReport[]>([]);
  const [customers, setCustomers] = useState<IFASAdditionalReport[]>([]);
  const [feedback, setFeedback] = useState<IFASAdditionalReport[]>([]);
  const [mortuaryTrends, setMortuaryTrends] = useState<IFASAdditionalReport[]>([]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
          canView("funeral") ? ifasReportsService.getFuneralRegisterReports(filter) : Promise.resolve([]),
          canView("planner") ? ifasReportsService.getPlannerPerformanceReports(filter) : Promise.resolve([]),
          canView("mortuary") ? ifasReportsService.getMortuaryOccupancyReports(filter) : Promise.resolve([]),
          canView("services") ? ifasReportsService.getServiceCateringReports(filter) : Promise.resolve([]),
          canView("fleet") ? ifasReportsService.getFleetLogisticsReports(filter) : Promise.resolve([]),
          canView("warehouse") ? ifasReportsService.getWarehouseInventoryReports(filter) : Promise.resolve([]),
          canView("executive") ? ifasReportsService.getExecutiveDashboard(filter) : Promise.resolve(null),
          canView("inventoryMovements") ? ifasReportsService.getInventoryMovementReports(filter) : Promise.resolve([]),
          canView("suppliers") ? ifasReportsService.getSupplierUtilisationReports(filter) : Promise.resolve([]),
          canView("customers") ? ifasReportsService.getCustomerAnalysisReports(filter) : Promise.resolve([]),
          canView("feedback") ? ifasReportsService.getFeedbackReports(filter) : Promise.resolve([]),
          canView("mortuaryTrends") ? ifasReportsService.getMortuaryTrendReports(filter) : Promise.resolve([]),
        ]);
      const fulfilled = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
        result.status === "fulfilled" ? result.value : fallback;
      setFunerals(fulfilled(results[0], []));
      setPlanners(fulfilled(results[1], []));
      setMortuaries(fulfilled(results[2], []));
      setServices(fulfilled(results[3], []));
      setFleet(fulfilled(results[4], []));
      setInventory(fulfilled(results[5], []));
      setDashboard(fulfilled(results[6], null));
      setInventoryMovements(fulfilled(results[7], []));
      setSuppliers(fulfilled(results[8], []));
      setCustomers(fulfilled(results[9], []));
      setFeedback(fulfilled(results[10], []));
      setMortuaryTrends(fulfilled(results[11], []));
      const failedCount = results.filter((result) => result.status === "rejected").length;
      if (failedCount > 0) {
        setError(`${failedCount} report section${failedCount === 1 ? "" : "s"} could not be loaded. Available report data is shown below.`);
      }
    } catch (err) {
      console.error("Failed to load IFAS reports:", err);
      setError("Reports could not be loaded. Check your access and connection, then try again.");
    } finally {
      setLoading(false);
    }
  }, [filter, canView]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === tabIndex)) setTabIndex(tabs[0]?.id || "");
  }, [effectiveRole, tabIndex, tabs]);

  const handleResetFilters = () => {
    setFilter({});
  };

  const handleReportTabChange = (_event: React.SyntheticEvent, value: string) => {
    setTabIndex(value);
    setFilter((current) => ({
      ...current,
      status: undefined,
      funeralType: undefined,
      ...(!["funeral", "services"].includes(value)
        ? { plannerName: undefined, funeralPlannerId: undefined, funeralDate: undefined }
        : {}),
    }));
  };

  return (
    <Box sx={{ background: ifasColors.page, minHeight: "100vh", pb: 6 }}>
      {/* Premium Hero Header */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(118deg, #101827 0%, #18243A 58%, #22233F 100%)",
          color: "#fff",
          pt: { xs: 2.5, md: 3 },
          pb: { xs: 5.5, md: 6.5 },
          px: { xs: 0, md: 1 },
          "&::after": { content: '""', position: "absolute", width: 360, height: 360, right: -80, top: -210, borderRadius: "50%", border: "80px solid rgba(236,110,0,.08)" },
        }}
      >
        <Container maxWidth="xl">
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ mb: 2, fontSize: "0.72rem", "& a": { color: "#A8B3C5" }, "& p": { color: "#FFFFFF" }, "& .MuiBreadcrumbs-separator": { color: "#64748B" } }}
          >
            <Link underline="hover" href="/dashboard">
              Dashboard
            </Link>
            <Link underline="hover" href="/dashboard">
              IFAS Workspace
            </Link>
            <Typography fontWeight={600}>Reporting Suite</Typography>
          </Breadcrumbs>

          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2.5} sx={{ position: "relative", zIndex: 1 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(236,110,0,.14)", display: "grid", placeItems: "center", border: "1px solid rgba(255,151,61,.22)" }}>
                  <AnalyticsIcon sx={{ fontSize: 22, color: "#FF8A25" }} />
                </Box>
                <Typography component="h1" sx={{ color: "#FFFFFF !important", fontSize: { xs: "1.55rem", md: "2rem" }, lineHeight: 1.15, fontWeight: 800, letterSpacing: "-0.035em" }}>
                  IFAS Reporting Module
                </Typography>
              </Stack>
              <Typography sx={{ color: "#C8D0DD !important", maxWidth: 760, fontSize: { xs: "0.78rem", md: "0.85rem" }, lineHeight: 1.65, ml: { md: 6.5 } }}>
                One reliable view of funeral operations, mortuary utilisation, catering, fleet logistics and warehouse inventory.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchReports}
              sx={{
                background: "linear-gradient(135deg, #EC6E00 0%, #FF8C00 100%)",
                color: "#fff",
                fontWeight: 700,
                px: 2.5, py: 1,
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "0.78rem",
                boxShadow: "0 8px 20px rgba(236,110,0,.25)",
                "&:hover": {
                  background: "linear-gradient(135deg, #d46300 0%, #e67e00 100%)",
                },
              }}
            >
              Refresh Real-time Data
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Main Container */}
      <Container maxWidth="xl" sx={{ mt: { xs: -3.5, md: -4 }, position: "relative", zIndex: 2 }}>
        {/* Global Filter Bar */}
        <IFASReportFilterBar
          filter={filter}
          onFilterChange={(newF) => setFilter(newF)}
          onReset={handleResetFilters}
          reportContext={tabIndex}
        />

        {error && (
          <Alert severity="warning" sx={{ mb: 2.5, borderRadius: "12px", border: "1px solid #FED7AA", bgcolor: "#FFF7ED" }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Navigation Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: "14px",
            p: 0.7,
            background: ifasColors.surface,
            border: `1px solid ${ifasColors.border}`,
            boxShadow: "0 5px 18px rgba(15,23,42,0.04)",
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={handleReportTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 46,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                fontWeight: 700,
                fontSize: "0.73rem",
                color: ifasColors.muted,
                borderRadius: "10px",
                py: 1.15,
                px: 2,
                minHeight: 42,
                textTransform: "none",
                transition: "all 0.2s ease",
                "& .MuiSvgIcon-root": { fontSize: 18 },
              },
              "& .Mui-selected": {
                background: ifasColors.softOrange,
                color: `${ifasColors.deepOrange} !important`,
              },
            }}
          >
            {(canView("funeral") || canView("planner")) && <Tab value="funeral" icon={<FuneralIcon />} iconPosition="start" label={`Funeral & Planner (${funerals.length})`} />}
            {(canView("mortuary") || canView("mortuaryTrends")) && <Tab value="mortuary" icon={<MortuaryIcon />} iconPosition="start" label={`Mortuary Utilisation (${mortuaries.length})`} />}
            {canView("services") && <Tab value="services" icon={<ServiceIcon />} iconPosition="start" label={`Catering & VAS (${services.length})`} />}
            {(canView("fleet") || canView("warehouse")) && <Tab value="fleetWarehouse" icon={<FleetIcon />} iconPosition="start" label={canView("fleet") && canView("warehouse") ? `Fleet & Inventory (${inventory.length})` : canView("fleet") ? `Fleet & Logistics (${fleet.length})` : `Warehouse & Inventory (${inventory.length})`} />}
            {canView("executive") && <Tab value="executive" icon={<ExecIcon />} iconPosition="start" label="Executive Dashboard" />}
            {(canView("inventoryMovements") || canView("suppliers") || canView("customers") || canView("feedback")) && <Tab value="additional" icon={<AdditionalIcon />} iconPosition="start" label="Additional Reports" />}
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        {tabIndex === "funeral" && (
          <FuneralManagementReportsTab
            funerals={funerals}
            planners={planners}
            loading={loading}
            filter={filter}
          />
        )}

        {tabIndex === "mortuary" && (
          <MortuaryReportsTab
            mortuaries={mortuaries}
            mortuaryTrends={mortuaryTrends}
            loading={loading}
            filter={filter}
          />
        )}

        {tabIndex === "services" && (
          <ServicesCateringReportsTab
            services={services}
            loading={loading}
            filter={filter}
          />
        )}

        {tabIndex === "fleetWarehouse" && (
          <FleetWarehouseReportsTab
            fleet={fleet}
            inventory={inventory}
            loading={loading}
            filter={filter}
            showFleet={canView("fleet")}
            showInventory={canView("warehouse")}
          />
        )}

        {tabIndex === "executive" && (
          <IFASExecutiveDashboardTab
            data={dashboard}
            loading={loading}
          />
        )}

        {tabIndex === "additional" && (
          <AdditionalReportsTab inventoryMovements={inventoryMovements} suppliers={suppliers}
            customers={customers} feedback={feedback} mortuaryTrends={mortuaryTrends}
            filter={filter} loading={loading}
            allowedReports={(["inventoryMovements", "suppliers", "customers", "feedback"] as IfasReportKey[]).filter(canView)} />
        )}
      </Container>
    </Box>
  );
};

export default IFASReportingHub;
